import { Component, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OrderService } from '../../../../_services/order.service';
import { AlertService } from '../../../../_services/alert.service';
import { OrderReturnsService } from '../../../../_services/order-returns.service';

interface ReturnItemRow {
  item: any;
  selected: boolean;
  quantity: number;
  condition: string;
}

@Component({
    selector: 'app-create-return',
    imports: [FormsModule, MatDialogContent, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
    templateUrl: './create-return.component.html',
    styleUrl: './create-return.component.css'
})
export class CreateReturnComponent {
  readonly dialogRef = inject(MatDialogRef<CreateReturnComponent>);

  orderNoSearch = '';
  loading = false;
  submitting = false;
  searched = false;
  order: any = null;
  rows: ReturnItemRow[] = [];
  reason = '';
  readonly conditionOptions = [
    { value: 'GOOD', label: 'Good - resellable' },
    { value: 'DAMAGED', label: 'Damaged' },
    { value: 'UNSELLABLE', label: 'Unsellable' },
  ];

  constructor(
    private orderService: OrderService,
    private orderReturnsService: OrderReturnsService,
    private alertService: AlertService,
  ) {}

  get selectedRows(): ReturnItemRow[] {
    return this.rows.filter((r) => r.selected);
  }

  get selectedCount(): number {
    return this.selectedRows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  }

  search(): void {
    const orderNo = Number(this.orderNoSearch);
    if (!orderNo) {
      this.alertService.alert('Enter a valid order number', 'error');
      return;
    }
    this.loading = true;
    this.searched = true;
    this.order = null;
    this.rows = [];
    this.orderService.getOrders({ orderNo, pageIndex: 0, pageSize: 1 }).subscribe({
      next: (response: any) => {
        const order = response?.orders?.[0];
        this.loading = false;
        if (!order) {
          this.alertService.alert(`No order found for #${orderNo}`, 'error');
          return;
        }
        this.order = order;
        this.rows = (order.OrderItems ?? []).map((item: any) => ({
          item,
          selected: false,
          quantity: item.quantity,
          condition: 'GOOD',
        }));
      },
      error: (error) => {
        this.loading = false;
        this.alertService.alert(error?.error?.message || 'Failed to search for order', 'error');
      },
    });
  }

  toggleRow(row: ReturnItemRow): void {
    row.selected = !row.selected;
    if (row.selected && (!row.quantity || row.quantity < 1)) row.quantity = row.item.quantity;
  }

  invalidQty(row: ReturnItemRow): boolean {
    return row.selected && (!row.quantity || row.quantity <= 0 || row.quantity > row.item.quantity);
  }

  onSubmit(): void {
    const selected = this.selectedRows;
    if (!selected.length) {
      this.alertService.alert('Select at least one item to return', 'error');
      return;
    }
    for (const row of selected) {
      if (this.invalidQty(row)) {
        this.alertService.alert(`Invalid quantity for ${row.item.productName}`, 'error');
        return;
      }
    }

    this.submitting = true;
    this.orderReturnsService
      .createOrderReturn({
        orderId: this.order.orderId,
        reason: this.reason,
        items: selected.map((row) => ({
          orderItemId: row.item.id,
          productVariantId: row.item.variantId,
          quantity: row.quantity,
          condition: row.condition,
        })),
      })
      .subscribe({
        next: () => {
          this.alertService.alert('Return created', 'success');
          this.dialogRef.close({ status: true });
        },
        error: (error) => {
          this.submitting = false;
          this.alertService.alert(error?.error?.message || 'Failed to create return', 'error');
        },
      });
  }
}
